import { P2GGrid } from '@parcel2go/core-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import RequirementsField from '~/components/core/forms/form-field-with-named-requirement';
import useQuoteRequirements from '~/hooks/use-quote-requirements';
import { TranslationNamespaces } from '~/lib/constants';
import { Account } from '~/lib/types/basket-types';
import { RequirementTypes } from '~/lib/types/enums';
import { IP2GBasketSection } from '~/hooks/use-basket-section';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';

export default function AccountDetails({
  section,
  errors
}: {
  section: IP2GBasketSection<Account>;
  errors: Record<string, string>;
}) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const { requirements } = useQuoteRequirements();
  const requirement = useMemo(() => requirements[RequirementTypes.ACCOUNT] ?? {}, [requirements]);
  const { settings } = useTenant();

  return (
    <P2GGrid container direction={'column'} spacing={2} dataTestId={'account-form'}>
      <P2GGrid item>
        <RequirementsField
          id={`txt-Account-forename`}
          defaultLabel={t(`ACCOUNT_FORENAME_LABEL`, { defaultValue: 'Forename' })}
          defaultRequired={true}
          defaultMaxLength={50}
          requirements={requirement}
          name="forename"
          translationKey={`ACCOUNT_FORENAME_LABEL`}
          data={section.data.forename || ''}
          update={(value) => section.update({ ...section.data, forename: value })}
          loading={section.loading}
          errors={errors}
          tabIndex={1}
          autoComplete="given-name"
        />
      </P2GGrid>
      <P2GGrid item>
        <RequirementsField
          id={`txt-Account-surname`}
          defaultLabel={t(`ACCOUNT_SURNAME_LABEL`, { defaultValue: 'Surname' })}
          defaultRequired={true}
          defaultMaxLength={50}
          requirements={requirement}
          name="surname"
          translationKey={`ACCOUNT_SURNAME_LABEL`}
          data={section.data.surname || ''}
          update={(value) => section.update({ ...section.data, surname: value })}
          loading={section.loading}
          errors={errors}
          tabIndex={2}
          autoComplete="family-name"
        />
      </P2GGrid>
      <P2GGrid item>
        <RequirementsField
          id={`txt-Account-email`}
          requirements={requirement}
          defaultLabel={t(`ACCOUNT_EMAIL_LABEL`, { defaultValue: 'Email' })}
          defaultRequired={true}
          defaultMaxLength={256}
          defaultType="email"
          name="email"
          translationKey={`ACCOUNT_EMAIL_LABEL`}
          data={section.data.email || ''}
          update={(value) => section.update({ ...section.data, email: value })}
          loading={section.loading}
          errors={errors}
          tabIndex={3}
          disabled={isAuthenticated}
          autoComplete="email"
        />
      </P2GGrid>
      {settings[PublicSettings.ACCOUNTMOBILE] === 'true' && (
        <P2GGrid item>
          <RequirementsField
            id={`txt-Account-mobile`}
            requirements={requirement}
            defaultLabel={t(`ACCOUNT_MOBILE_LABEL`, { defaultValue: 'Mobile Number' })}
            defaultMaxLength={14}
            defaultMinLength={section.data.mobile ? 10 : undefined}
            defaultType="tel"
            name="mobile"
            translationKey={`ACCOUNT_MOBILE_LABEL`}
            data={section.data.mobile || ''}
            update={(value) => section.update({ ...section.data, mobile: value })}
            loading={section.loading}
            errors={errors}
            tabIndex={4}
            autoComplete="mobile"
          />
        </P2GGrid>
      )}
    </P2GGrid>
  );
}
